import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, Input, Popconfirm, Select, Space,
} from 'antd';
import zoneStore from '../../Store';
import selectionStore, { clearSelection } from '../../Store/selectionStore';
import defaultValues from '../../../utils/defaultValues';
import {
  applyStartFlag, findScreen, getScreens, removeScreen, replaceScreen, saveZone,
} from '../../../utils/zoneMutations';
import FieldLabel, { PanelTitle } from './FieldLabel';
import AdvancedBlock from './AdvancedBlock';
import RichTextField from './RichTextField';
import ButtonList from './ButtonList';

const INPUT_TYPES = ['inputScreen', 'multiScreen', 'addressScreen'];
const BOTTOM_TYPES = ['infoScreen', 'startScreen'];

function ScreenPanel() {
  const screens = zoneStore.useState((s) => s.config.screens);
  const screenId = selectionStore.useState((s) => s.screenId);
  const screen = findScreen(screens, screenId);

  const [formData, setFormData] = useState(screen || {});

  useEffect(() => {
    setFormData(findScreen(getScreens(), screenId) || {});
  }, [screenId]);

  if (!screen) return null;

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const apply = async () => {
    const updated = { ...screen, ...formData };
    let nextScreens = replaceScreen(getScreens(), updated);

    if (updated.isStart) {
      nextScreens = applyStartFlag(nextScreens, updated.screenId);
    }

    await saveZone(nextScreens, 'Экран сохранён');
  };

  const deleteScreen = async () => {
    const ok = await saveZone(removeScreen(getScreens(), screenId), 'Экран удалён');
    if (ok) clearSelection();
  };

  // Кнопки других экранов, которые ведут на этот — их придётся отвязать при удалении
  const incoming = screens.filter((item) => item.screenId !== screenId)
    .flatMap((item) => (item.buttons || [])
      .filter((btn) => btn.path === screenId)
      .map((btn) => `${item.headTitle || item.screenId} → «${btn.name}»`));

  const deleteTitle = incoming.length
    ? `На этот экран ведут кнопки: ${incoming.join(', ')}. Они станут «без действия». Удалить?`
    : 'Удалить экран?';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <PanelTitle>Экран</PanelTitle>

      <div>
        <FieldLabel>Наименование (для админа)</FieldLabel>
        <Input name="headTitle" value={formData.headTitle || ''} onChange={handleInput} />
      </div>

      <div>
        <FieldLabel>Заголовок на экране</FieldLabel>
        <Input name="title" value={formData.title || ''} onChange={handleInput} />
      </div>

      <div>
        <FieldLabel>Подзаголовок</FieldLabel>
        <Input name="description" value={formData.description || ''} onChange={handleInput} />
      </div>

      <div>
        <FieldLabel>Тип экрана</FieldLabel>
        <Select
          style={{ width: '100%' }}
          value={formData.type}
          onChange={(type) => setFormData((prev) => ({ ...prev, type }))}
          options={defaultValues.screenTypes}
        />
      </div>

      <Checkbox
        checked={Boolean(formData.isStart)}
        onChange={(e) => setFormData((prev) => ({ ...prev, isStart: e.target.checked }))}
      >
        Стартовый экран зоны
      </Checkbox>

      <ButtonList screen={screen} />

      {BOTTOM_TYPES.includes(formData.type) && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <PanelTitle>
            {formData.type === 'infoScreen' ? 'Тексты экрана' : 'Нижний блок'}
          </PanelTitle>
          <RichTextField
            label="Верхний текст"
            value={formData.bottomBlock?.topText}
            onChange={(value) => setFormData((prev) => ({
              ...prev,
              bottomBlock: { ...prev.bottomBlock, topText: value },
            }))}
          />
          <RichTextField
            label="Нижний текст"
            value={formData.bottomBlock?.bottomText}
            onChange={(value) => setFormData((prev) => ({
              ...prev,
              bottomBlock: { ...prev.bottomBlock, bottomText: value },
            }))}
          />
        </Space>
      )}

      <AdvancedBlock>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <FieldLabel>Идентификатор экрана</FieldLabel>
            <Input
              name="screenSubType"
              value={formData.screenSubType || ''}
              onChange={handleInput}
            />
          </div>
          <div>
            <FieldLabel>Перевод заголовка</FieldLabel>
            <Input
              name="headTitle_eng"
              value={formData.headTitle_eng || ''}
              onChange={handleInput}
            />
          </div>
          {formData.type === 'infoScreen' && (
            <div>
              <FieldLabel>Информационный текст</FieldLabel>
              <Input.TextArea
                name="help"
                value={formData.help || ''}
                onChange={handleInput}
                rows={3}
              />
            </div>
          )}
          {INPUT_TYPES.includes(formData.type) && (
            <FieldLabel>
              Доп. поле и переход задаются на старой странице — см. «Открыть в старом редакторе»
            </FieldLabel>
          )}
        </Space>
      </AdvancedBlock>

      <Space>
        <Button type="primary" onClick={apply} disabled={!formData.headTitle}>
          Применить
        </Button>
        <Popconfirm
          title={deleteTitle}
          onConfirm={deleteScreen}
          okText="Удалить"
          cancelText="Отмена"
        >
          <Button danger>Удалить</Button>
        </Popconfirm>
      </Space>
    </Space>
  );
}

export default ScreenPanel;
